/* eslint-disable @typescript-eslint/ban-ts-comment,no-param-reassign */
import { fabric } from "fabric";
import { remove, handle } from "./icons";
import {
  FabricCanvasObject,
  FabricObject,
  Mapping,
  RegionData,
  TextBox,
  SpecialOptions,
} from "~/CustomizableToolPage/types";
import { FabricTypes } from "~/CustomizableToolPage/fabric/constants";
import {
  TEXT_BOX_MAX_FONT_SIZE,
  TEXT_BOX_PLACEHOLDER_TEXT,
} from "~/CustomizableToolPage/CustomizableTool/constants";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
/* const addDebugging = (canvas: fabric.Canvas) => {
  canvas.on("after:render", () => {
    canvas.contextContainer.strokeStyle = "#555";

    canvas.forEachObject((obj) => {
      const bound = obj.getBoundingRect();

      canvas.contextContainer.strokeRect(
        bound.left + 0.5,
        bound.top + 0.5,
        bound.width,
        bound.height
      );
    });
  });
}; */

export const createUniqueIdentifier = () =>
  Math.random().toString(36).substr(2, 9);

export const getCanvasDimensions = (region: RegionData) => {
  const canvasSquareDefaultDimensions = 375;
  // set a minimum dimension length if half the default to not allow for unwanted narrow canvases
  const canvasMinimumDimensionLength = canvasSquareDefaultDimensions / 2;

  let canvasWidth = canvasSquareDefaultDimensions;
  let canvasHeight = canvasSquareDefaultDimensions;

  if (region?.id) {
    const { height, width } = region.mapping;

    if (width > height) {
      canvasHeight = (height / width) * canvasWidth;
    } else {
      canvasWidth = (width / height) * canvasHeight;
    }
  }

  // reset the canvas dimensions to a minimum of they are too narrow
  if (canvasHeight < canvasMinimumDimensionLength) {
    canvasWidth *= canvasMinimumDimensionLength / canvasHeight;
    canvasHeight = canvasMinimumDimensionLength;
  } else if (canvasWidth < canvasMinimumDimensionLength) {
    canvasHeight *= canvasMinimumDimensionLength / canvasWidth;
    canvasWidth = canvasMinimumDimensionLength;
  }

  return { height: canvasHeight, width: canvasWidth };
};

// 24 was the old default
const handleIconSize = 24;
const handleIcon = document.createElement("img");
handleIcon.src = handle;

const removeIconSize = 34;
const removeIcon = document.createElement("img");
removeIcon.src = remove;

export const getTextBoxes = (canvas: fabric.Canvas) =>
  getCustomizations(canvas).filter(({ type }) => type === FabricTypes.textBox);

export const getImages = (canvas: fabric.Canvas) =>
  getCustomizations(canvas).filter(({ type }) => type === FabricTypes.image);

export const getCustomizations = (canvas: fabric.Canvas) =>
  canvas
    .getObjects()
    .filter(({ type = "" }) =>
      [FabricTypes.textBox, FabricTypes.image].includes(type)
    );

export const addText = (
  canvas: fabric.Canvas,
  activeRegion: RegionData,
  customConditions: {
    yPercentage: number;
  }
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const textBox = new fabric.IText(TEXT_BOX_PLACEHOLDER_TEXT, {
    clientIdentifier: createUniqueIdentifier(),
    fontSize: TEXT_BOX_MAX_FONT_SIZE,
    textAlign: "center",
    fill: "black",
    fontFamily: "Arial",
    top:
      getCanvasDimensions(activeRegion!).height * 0.5 -
      // eslint-disable-next-line no-underscore-dangle
      (fabric.IText.prototype._fontSizeMult * TEXT_BOX_MAX_FONT_SIZE) / 2,
    // left:
    //  getCanvasDimensions(state.activeRegion).width * 0.5 -
    //  fabricText.getTextWidth("px", state.canvas) / 2,
    lockScalingFlip: true,
    isPlaceholder: true,
  } as TextBox);

  // This allows us to setup additional settings upon creation of this
  // TODO: This does not look used in the old code - add it back if that turns out to be inaccurate.
  // if (additionalSettings) textBox.set(additionalSettings);
  // We can go through our customConditions and setup even more settings (initial location of text boxes, etc);
  if (customConditions) {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const condition in customConditions) {
      switch (condition) {
        case "yPercentage":
          if (customConditions[condition] !== 0.5) {
            textBox.set(
              "top",
              getCanvasDimensions(activeRegion!).height *
                customConditions[condition]
            );
          }
          break;
        default:
          break;
      }
    }
  }

  canvas.centerObjectH(textBox);
  canvas.add(textBox);
};

export const addImage = (
  canvas: fabric.Canvas,
  src: string,
  assetId: number
) => {
  const existingImages = canvas
    ?.toObject(["clientIdentifier"])
    .objects.filter((o: fabric.Object) => o.type === FabricTypes.image)
    .map((i: FabricObject) => i.clientIdentifier);

  if (existingImages?.length) {
    deleteByClientIdentifiers(canvas, existingImages);
  }

  fabric.Image.fromURL(
    src,
    (image: fabric.Object) => {
      const maxImageDimension = Math.max(image.width ?? 0, image.height ?? 0);
      const minCanvasDimension = Math.min(
        canvas?.getWidth() || 0,
        canvas?.getHeight() || 0
      );

      const scaleAmt = minCanvasDimension / maxImageDimension; // fit to canvas
      image.scale(scaleAmt * 0.85); // Scale down so that controls aren't off canvas

      canvas.centerObject(image);
      canvas.add(image);
      canvas.renderAll();
    },
    {
      // @ts-ignore
      clientIdentifier: createUniqueIdentifier(),
      isPlaceholder: false,
      crossOrigin: "anonymous",
      assetId,
    }
  );
  canvas.renderAll();
};

export const deleteByClientIdentifiers = (
  canvas: fabric.Canvas,
  clientIdentifiers: string[]
) => {
  clientIdentifiers.forEach((id) => {
    const canvasElement = getObjectByClientIdentifier(canvas, id);
    if (canvasElement) {
      canvas.remove(canvasElement);
    }

    canvas.calcOffset().renderAll();
  });
};

export const getObjectByClientIdentifier = (
  canvas: fabric.Canvas,
  clientIdentifier: string
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const objectArr: FabricObject[] = canvas.getObjects() as FabricObject[];
  // eslint-disable-next-line no-restricted-syntax
  for (const i in objectArr) {
    if (
      !!objectArr[i]?.clientIdentifier &&
      objectArr[i]!.clientIdentifier === clientIdentifier
    )
      return objectArr[i];
  }
  return undefined;
};

// @ts-ignore
export const doesRegionHaveCustomizations = (region) =>
  // @ts-ignore
  !!region?.objects.filter((o) => !o.isPlaceholder).length;

export const handleRotateLimit = (obj: FabricObject) => {
  if (!obj.previousAngle) {
    obj.previousAngle = obj.angle;
    obj.previousLeft = obj.left;
    obj.previousTop = obj.top;
    obj.previousHeight = obj.height;
    obj.previousWidth = obj.width;
    obj.previousScaleX = obj.scaleX;
    obj.previousScaleY = obj.scaleY;
  }

  const rotatingDirection =
    (obj.previousAngle ?? 0) - (obj.angle ?? 0) < 0 ? "right" : "left";

  // if we are changing the angle, but the new angle is going to put the bounding box over the edge of the canvas, don't rotate.
  // third condition is if we are still trying to rotate in the same direction that put us into the boundary.  we can go the other direction.
  if (
    obj.previousAngle !== obj.angle &&
    !canUpdateAngle(obj) &&
    (!obj.rotatingDirection || rotatingDirection === obj.rotatingDirection)
  ) {
    obj.angle = obj.previousAngle;
    obj.left = obj.previousLeft;
    obj.top = obj.previousTop;
    obj.height = obj.previousHeight;
    obj.width = obj.previousWidth;
    obj.scaleX = obj.previousScaleX;
    obj.scaleY = obj.previousScaleY;
    obj.rotatingDirection = rotatingDirection;
  }

  // If we changed direction, clear the previous rotating direction so we can go back.
  if (obj.rotatingDirection && rotatingDirection !== obj.rotatingDirection) {
    obj.rotatingDirection = undefined;
  }

  obj.previousAngle = obj.angle;
  obj.previousLeft = obj.left;
  obj.previousTop = obj.top;
  obj.previousHeight = obj.height;
  obj.previousWidth = obj.width;
  obj.previousScaleX = obj.scaleX;
  obj.previousScaleY = obj.scaleY;
};

export const canUpdateAngle = (target: fabric.Object) => {
  if (!target) {
    return false;
  }

  const boundingBox = target.getBoundingRect();

  if (
    boundingBox.left < 0 ||
    boundingBox.top < 0 ||
    boundingBox.left + boundingBox.width > (target.canvas?.width ?? 0) ||
    boundingBox.top + boundingBox.height > (target.canvas?.height ?? 0)
  ) {
    return false;
  }

  return true;
};

export const handleScalingLimit = (target: FabricObject) => {
  if (!target) {
    return;
  }

  const { top, left, height, width } = target.getBoundingRect();
  const embiggening = (target.scaleX ?? 0) > (target.previousScaleX ?? 0);

  if (
    embiggening &&
    (left < 0 || // hit left
      top < 0 || // hit top
      left + width > (target.canvas?.width ?? 0) || // hit right
      top + height > (target.canvas?.height ?? 0)) // hit bottom
  ) {
    // load from prev values
    target.top = target.previousTop;
    target.left = target.previousLeft;
    target.scaleX = target.previousScaleX;
    target.scaleY = target.previousScaleY;
  } else {
    // save prev values and continue
    target.previousTop = target.top ?? 0;
    target.previousLeft = target.left ?? 0;
    target.previousScaleX = target.scaleX ?? 0;
    target.previousScaleY = target.scaleY ?? 0;
  }
};

export const handleMoveLimit = (obj: fabric.Object) => {
  obj.setCoords();

  // top-left  corner
  if (obj.getBoundingRect().top < 0 || obj.getBoundingRect().left < 0) {
    obj.top = Math.max(obj.top!, obj.top! - obj.getBoundingRect().top);
    obj.left = Math.max(obj.left!, obj.left! - obj.getBoundingRect().left);
    obj.setCoords();
  }

  // bot-right corner
  if (
    obj.getBoundingRect().top + obj.getBoundingRect().height >
      obj.canvas!.height! ||
    obj.getBoundingRect().left + obj.getBoundingRect().width >
      obj.canvas!.width!
  ) {
    obj.top = Math.min(
      obj.top!,
      obj.canvas!.height! -
        obj.getBoundingRect().height +
        obj.top! -
        obj.getBoundingRect().top
    );
    obj.left = Math.min(
      obj.left!,
      obj.canvas!.width! -
        obj.getBoundingRect().width +
        obj.left! -
        obj.getBoundingRect().left
    );
    obj.setCoords();
  }
};

export const configureCanvasOptions = (canvas: fabric.Canvas) => {
  fabric.Object.prototype.set({
    borderColor: "#96c874",
    borderScaleFactor: 3,
    lockScalingFlip: true,
    // cornerSize: 35, // This affects the "hitbox" of buttons
  });

  fabric.Object.prototype.controls = {
    delete: new fabric.Control({
      x: 0.5,
      y: -0.5,
      offsetY: 0,
      offsetX: 0,
      cursorStyle: "pointer",
      render: (ctx, left, top, styleOverride, fabricObject) => {
        const size = removeIconSize;
        ctx.save();
        ctx.translate(left, top);
        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle!));
        ctx.drawImage(removeIcon, -size / 2, -size / 2, size, size);
        ctx.restore();
      },
      mouseUpHandler: (eventData: MouseEvent, transform: fabric.Transform) => {
        const { target } = transform;
        canvas.remove(target);
        canvas.requestRenderAll();
        return true;
      },
    }),

    rotate: new fabric.Control({
      x: 0,
      y: -0.5,
      offsetY: -40,
      cursorStyle: "pointer",
      // @ts-ignore
      actionHandler: fabric.controlsUtils.rotationWithSnapping,
      actionName: "rotate",
      render: (ctx, left, top) => {
        const size = handleIconSize;
        ctx.save();
        ctx.translate(left, top);
        ctx.drawImage(handleIcon, -size / 2, -size / 2, size, size);
        ctx.restore();
      },
      withConnection: true,
    }),
    scale: new fabric.Control({
      x: 0.5,
      y: 0.5,
      // @ts-ignore
      cursorStyleHandler: fabric.controlsUtils.scaleCursorStyleHandler,
      // @ts-ignore
      actionHandler: fabric.controlsUtils.scalingEqually,
      actionName: "scale",
      render: (ctx, left, top) => {
        const size = handleIconSize;
        ctx.save();
        ctx.translate(left, top);
        ctx.drawImage(handleIcon, -size / 2, -size / 2, size, size);
        ctx.restore();
      },
    }),
  };

  fabric.IText.prototype.setControlsVisibility({
    // @ts-ignore
    delete: false,
    rotate: false,
  });
};

export const scaleFabricObjFor3DRender = (
  fabricObject: fabric.Object,
  canvas: fabric.Canvas,
  mapping: Mapping
) => {
  // TODO: investigate editableRegionStrokeWidth
  const editableRegionStrokeWidth = 5;

  // We need to get percentages for our original object based upon the region
  const previousScaledDimensions = {
    left: fabricObject.left! / canvas.width!,
    top: fabricObject.top! / canvas.height!,
  };

  // At this point, we have scaled percentage dimensions based upon the editable canvas size
  // We now need to take these percentages and figure out our new location based upon our current object
  const newDimensionsBasedUponMapping = {
    left:
      mapping.left +
      editableRegionStrokeWidth +
      mapping.width * previousScaledDimensions.left,
    top:
      mapping.top +
      editableRegionStrokeWidth +
      mapping.height * previousScaledDimensions.top,
  };

  // apply them...
  fabricObject.left = newDimensionsBasedUponMapping.left;
  fabricObject.top = newDimensionsBasedUponMapping.top;

  // @mapping is basically the dimensions of our new canvas, @canvas is the canvas we're transposing from
  const widthScale =
    (mapping.width - editableRegionStrokeWidth) / canvas.width!;

  const heightScale =
    (mapping.height - editableRegionStrokeWidth) / canvas.height!;

  const scale = Math.min(widthScale, heightScale);

  fabricObject.scaleX! *= scale;
  fabricObject.scaleY! *= scale;

  if (fabricObject.type === FabricTypes.textBox) {
    fabricObject.fill = "white";
    fabricObject.strokeWidth = 4;
    fabricObject.stroke = "black";
    fabricObject.paintFirst = "stroke";
  }

  return fabricObject;
};

export const getCanvasRegionOutline = (
  isAlpha: boolean,
  mapping: Mapping,
  isActiveRegion: boolean
) => {
  return new fabric.Rect({
    visible: isActiveRegion,
    originX: "left",
    originY: "top",
    top: mapping.top,
    left: mapping.left,
    width: mapping.width,
    height: mapping.height,
    stroke: isAlpha ? "white" : "#4ba521",
    strokeDashArray: [10, 5],
    strokeWidth: 5,
    fill: "rgba(0,0,0,0)",
    // @ts-ignore
    clientIdentifier: `coordinate-${mapping.coordinateId}`,
    coordinateId: mapping.coordinateId,
  });
};

export const handleTextBoxChange = (
  target: FabricCanvasObject,
  specialOptions: SpecialOptions
) => {
  // @ts-ignore
  target.text = target.text?.replace(/(\r\n|\n|\r)/gm, "");

  if (specialOptions.numericOnly) {
    // @ts-ignore
    target.text = target.text.replace(/[^0-9]/g, "");
  }

  if (specialOptions.maxCharacters) {
    // @ts-ignore
    while (target.text.length > specialOptions.maxCharacters) {
      // @ts-ignore
      target.text = target.text.slice(0, -1);
    }
  }

  // @ts-ignore
  target.selectionStart = target.text.length;
  // @ts-ignore
  target.selectionEnd = target.text.length;

  // @ts-ignore
  if (target.text.length === 0) {
    // @ts-ignore
    target.isPlaceholder = true;
  } else {
    // @ts-ignore
    target.isPlaceholder = false;
  }
};
